import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { SectionCard } from "@/components/gooseberry-output/SectionCard";

function sourceStatusLabel(status, language = "en") {
  const nl = language === "nl";
  if (status === "live" || status === "live-search") return nl ? "Live/publieke metadata" : "Live/public metadata";
  if (status === "local-codebook") return nl ? "Lokaal codebook" : "Local codebook";
  if (status === "selected-open-dataset") return nl ? "Geselecteerde open dataset" : "Selected open dataset";
  if (status === "curated") return nl ? "Publieke fallback metadata" : "Curated public metadata";
  return status || "Metadata";
}

export function SourcesAndConfidence({ parsed, sourcesUsed, feedbackContext, modelInfo = {}, groundingInfo = {}, language = "en" }) {
  const nl = language === "nl";
  const dbSection = parsed.bySection["databases used"];
  const overall = parsed.bySection["overall confidence"];
  return (
    <div className="space-y-3" data-testid="synberry-output-sources-confidence">
      {overall && <SectionCard section={overall} feedbackContext={feedbackContext} language={language} />}
      {(modelInfo.analysisModel || Object.keys(groundingInfo || {}).length > 0) && (
        <div className="rounded-2xl border border-black/10 bg-white p-5">
          <h3 className="font-display text-base font-semibold text-black">{nl ? "Model- en onderbouwingsdetails" : "Model and grounding details"}</h3>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-black/70">
            <div>{nl ? "Analysemodel" : "Analysis model"}: <strong className="text-black">{modelInfo.analysisModel || (nl ? "niet gerapporteerd" : "not reported")}</strong></div>
            <div>{nl ? "Evidence-check" : "Evidence check"}: <strong className="text-black">{modelInfo.evidenceModel || (nl ? "uit" : "off")}</strong></div>
            <div>{nl ? "Lokale codebooks" : "Local codebooks"}: <strong className="text-black">{groundingInfo.usesLocalCodebooks ? `${groundingInfo.localCodebookSnippetCount || 0} ${nl ? "fragmenten" : "excerpts"}` : (nl ? "geen match" : "no match")}</strong></div>
            <div>{nl ? "Raw paneldata" : "Raw panel data"}: <strong className="text-black">{nl ? "niet gebruikt" : "not used"}</strong></div>
          </div>
        </div>
      )}
      {sourcesUsed && sourcesUsed.length > 0 && (
        <div className="rounded-2xl border border-black/10 bg-white p-5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-display text-base font-semibold text-black">{nl ? "Gebruikte databronnen" : "Data Sources Used"}</h3>
            <Badge variant="outline" className="rounded-full border-[#61CBA3] text-[#61CBA3]">
              {sourcesUsed.length} {nl ? `bron${sourcesUsed.length === 1 ? "" : "nen"}` : `source${sourcesUsed.length === 1 ? "" : "s"}`}
            </Badge>
          </div>
          <ul className="mt-3 space-y-3">
            {sourcesUsed.map((s, idx) => (
              <li key={`${s.url || s.title}-${idx}`} className="rounded-xl border border-black/10 p-4 bg-white">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-full border-[#61CBA3] text-[#61CBA3]">{s.source || "Other"}</Badge>
                    {s.sourceStatus && (
                      <Badge className={`rounded-full border ${(s.sourceStatus === "live" || s.sourceStatus === "live-search" || s.sourceStatus === "local-codebook") ? "border-[#61CBA3] bg-[rgba(97,203,163,0.22)]" : "border-black/15 bg-white"} text-black`}>
                        {sourceStatusLabel(s.sourceStatus, language)}
                      </Badge>
                    )}
                  </div>
                  {s.url && (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#61CBA3] underline underline-offset-4 hover:text-[#4DAF8B]">
                      {nl ? "Open bron" : "Open source"} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                <div className="mt-2 font-display font-semibold text-black">{s.title || s.url}</div>
                {s.description && <div className="mt-1 text-sm text-black/70">{s.description}</div>}
                {s.accessNote && <div className="mt-2 text-xs text-black/55">{s.accessNote}</div>}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-black/55">
            {nl
              ? "Bronnen worden alleen gebruikt als geaggregeerde/contextuele onderbouwing. GooseBerry analyseert geen raw LISS Panel-data of datasets met beperkte toegang."
              : "Sources are used as aggregate/contextual grounding only. GooseBerry does not analyze raw LISS Panel data or permission-restricted datasets."}
          </p>
        </div>
      )}
      {dbSection && (
        <details className="rounded-2xl border border-black/10 bg-white p-5">
          <summary className="cursor-pointer text-sm text-[#61CBA3] hover:underline list-none flex items-center gap-1">
            <ChevronDown className="h-4 w-4" /> {nl ? "Bekijk ruwe modelcontext voor bronnen" : "See raw model context for sources"}
          </summary>
          <div className="mt-3 prose-markdown text-black">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}>
              {dbSection.body}
            </ReactMarkdown>
          </div>
        </details>
      )}
    </div>
  );
}
